import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

interface DeletedBill {
  id: number;
  /** NULL for consumables — invoice_no is their number. */
  bill_no: number | null;
  invoice_no?: string | null;
  bill_date: string;
  invoice_period_start: string;
  invoice_period_end: string;
  total_amount: number;
  status: string;
  bill_type: string | null;
  patient_id: number;
  patient_name: string;
  client_name: string | null;
  deleted_at: string | null;
}

const fmt = (d: string) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtAmt = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DeletedBills: React.FC = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState<DeletedBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [restoring, setRestoring] = useState<number | null>(null);

  const token = localStorage.getItem('accessToken');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchDeleted = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/billing/deleted-bills`, { headers });
      setBills(res.data);
    } catch {
      toast.error('Failed to load deleted bills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);

  const handleRestore = async (bill: DeletedBill) => {
    if (!window.confirm(`Restore Bill #${bill.bill_no} for ${bill.patient_name}?`)) return;
    setRestoring(bill.id);
    try {
      await axios.post(`${API_BASE_URL}/api/billing/bills/${bill.id}/restore`, {}, { headers });
      setBills((prev) => prev.filter((b) => b.id !== bill.id));
      toast.success(`Bill #${bill.bill_no} restored`);
    } catch {
      toast.error('Failed to restore bill');
    } finally {
      setRestoring(null);
    }
  };

  const filtered = bills.filter((b) => {
    const t = searchTerm.toLowerCase();
    return (
      b.patient_name.toLowerCase().includes(t) ||
      (b.client_name || '').toLowerCase().includes(t) ||
      String(b.bill_no ?? '').includes(t) ||
      (b.invoice_no || '').toLowerCase().includes(t) ||
      `LHHS${b.patient_id}`.toLowerCase().includes(t)
    );
  });

  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2rem' },
    title: { margin: 0, fontSize: '1.85rem', color: '#0f172a', fontWeight: 700 },
    subtitle: { margin: '4px 0 0', fontSize: '0.9rem', color: '#64748b' },
    searchInput: {
      padding: '0.65rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1',
      fontSize: '0.9rem', width: '300px', outline: 'none',
    },
    card: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    tableHeader: {
      padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    },
    table: { width: '100%', borderCollapse: 'collapse' as const },
    th: {
      padding: '0.75rem 1rem', textAlign: 'left' as const, fontSize: '0.72rem',
      fontWeight: 600, color: '#64748b', textTransform: 'uppercase' as const,
      letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc',
    },
    td: { padding: '0.9rem 1rem', fontSize: '0.88rem', color: '#334155', borderBottom: '1px solid #f1f5f9' },
    emptyState: { textAlign: 'center' as const, padding: '3rem', color: '#94a3b8' },
    cid: { fontSize: '0.78rem', color: '#64748b', fontFamily: 'monospace' },
    deletedAt: { fontSize: '0.78rem', color: '#ef4444', marginTop: '2px' },
    restoreBtn: {
      padding: '0.4rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer',
      fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#dcfce7', color: '#15803d',
    },
    viewBtn: {
      padding: '0.4rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer',
      fontSize: '0.78rem', fontWeight: 600, backgroundColor: '#dbeafe', color: '#1e40af',
    },
  };

  const statusBadge = (status: string): React.CSSProperties => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '9999px',
    fontSize: '0.75rem', fontWeight: 600,
    backgroundColor: status === 'Collected' ? '#dcfce7' : status === 'Processed' ? '#dbeafe' : '#fef3c7',
    color: status === 'Collected' ? '#15803d' : status === 'Processed' ? '#1e40af' : '#92400e',
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Deleted Bills</h1>
          <p style={styles.subtitle}>All soft-deleted bills — restore to move them back to active billing</p>
        </div>
        <input
          type="text"
          placeholder="Search by name, bill no, CID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      <div style={styles.card}>
        <div style={styles.tableHeader}>
          <span style={{ fontWeight: 600, color: '#0f172a' }}>
            Deleted Bills ({filtered.length})
          </span>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={styles.emptyState}>
            {bills.length === 0 ? 'No deleted bills.' : 'No bills match your search.'}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Bill No', 'Client / Patient', 'Invoice Period', 'Amount', 'Status', 'Deleted On', ''].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((bill) => (
                <tr
                  key={bill.id}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {bill.bill_type === 'consumable_return'
                      ? bill.invoice_no || `R-${bill.bill_no}`
                      : bill.invoice_no || `#${bill.bill_no}`}
                  </td>
                  {/* Client leads — it's who the bill is raised against — with
                      the patient underneath, matching the billing screens.
                      Cases with no client on record fall back to the patient
                      alone rather than showing an empty line. */}
                  <td style={styles.td}>
                    <div style={{ fontWeight: 600 }}>{bill.client_name || bill.patient_name}</div>
                    <div style={styles.cid}>
                      LHHS{bill.patient_id}{bill.client_name ? ` · ${bill.patient_name}` : ''}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {fmt(bill.invoice_period_start)} — {fmt(bill.invoice_period_end)}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>₹{fmtAmt(bill.total_amount)}</td>
                  <td style={styles.td}>
                    <span style={statusBadge(bill.status)}>{bill.status}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '0.85rem', color: '#ef4444' }}>
                      {bill.deleted_at ? fmt(bill.deleted_at) : '—'}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        style={styles.viewBtn}
                        onClick={() => {
                          console.log('Bill Type:', bill.bill_type);
                          if (bill.bill_type === 'consumables' || bill.bill_type === 'consumable') {
                            navigate(`/billing/consumables/edit/${bill.id}`, { state: { from: 'deleted' } });
                          } else if (bill.bill_type === 'consumable_return') {
                            navigate(`/billing/consumables/return/edit/${bill.id}`, { state: { from: 'deleted' } });
                          } else {
                            navigate(`/billing/bill/${bill.id}`, { state: { from: 'deleted' } });
                          }
                        }}
                      >
                        View
                      </button>
                      <button
                        style={styles.restoreBtn}
                        disabled={restoring === bill.id}
                        onClick={() => handleRestore(bill)}
                      >
                        {restoring === bill.id ? '...' : 'Restore'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DeletedBills;
